'use client';
import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import NavigationProgress from './NavigationProgress';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { usePathname, useRouter } from 'next/navigation';

interface AppLayoutProps {
  children: React.ReactNode;
}

function AppLayoutInner({ children }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Redirect LLA-only roles away from LRA workspace to their workspace
  useEffect(() => {
    if (!user) return;
    const llaOnlyRoles = ['lla_staff', 'field_enumerator', 'county_admin'];
    if (llaOnlyRoles.includes(user.role)) {
      router.replace('/lla/parcel-registry');
    }
  }, [user, router]);

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: 'var(--dark-bg)' }}>
      <NavigationProgress />

      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((p) => !p)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Main content */}
      <main
        className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin transition-all duration-300 w-full"
        style={{ backgroundColor: 'var(--dark-bg)' }}
      >
        {/* Mobile top bar */}
        <div
          className="flex md:hidden items-center gap-3 px-4 py-3 border-b sticky top-0 z-30"
          style={{
            backgroundColor: 'var(--dark-surface)',
            borderColor: 'var(--dark-border)',
          }}
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="flex flex-col gap-1.5 p-1.5 rounded-md hover:bg-white/10 transition-colors"
            aria-label="Open menu"
          >
            <span className="block w-5 h-0.5 rounded" style={{ backgroundColor: 'var(--dark-text)' }} />
            <span className="block w-5 h-0.5 rounded" style={{ backgroundColor: 'var(--dark-text)' }} />
            <span className="block w-5 h-0.5 rounded" style={{ backgroundColor: 'var(--dark-text)' }} />
          </button>
          <div className="flex flex-col">
            <span className="text-sm font-bold leading-tight" style={{ color: 'var(--dark-text)' }}>
              LRA Nexus
            </span>
            <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              Revenue Intelligence
            </span>
          </div>
        </div>

        {children}
      </main>
    </div>
  );
}

export default function AppLayout({ children }: AppLayoutProps) {
  return (
    <AuthProvider>
      <AppLayoutInner>{children}</AppLayoutInner>
    </AuthProvider>
  );
}